import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from 'recharts';

const CampaignEffectivenessChart = ({ campaigns }) => {
  // Process campaign data for the chart
  const processData = () => {
    if (!campaigns || campaigns.length === 0) {
      return [];
    }

    // Group campaigns by type and calculate effectiveness
    const campaignsByType = {};
    
    campaigns.forEach(campaign => {
      const type = campaign.campaignType || 'general';
      
      if (!campaignsByType[type]) {
        campaignsByType[type] = {
          type,
          totalContacts: 0,
          respondedContacts: 0,
          convertedContacts: 0,
          campaigns: 0
        };
      }
      
      // Get contacts for this campaign
      const campaignContacts = JSON.parse(localStorage.getItem(`campaign_contacts_${campaign.id}`)) || [];
      
      // Count total, responded, and converted contacts
      campaignsByType[type].totalContacts += campaignContacts.length;
      campaignsByType[type].respondedContacts += campaignContacts.filter(c => c.status === 'responded').length;
      campaignsByType[type].convertedContacts += campaignContacts.filter(c => c.status === 'converted').length;
      campaignsByType[type].campaigns += 1;
    });
    
    // Convert to array and calculate percentages
    return Object.values(campaignsByType).map(typeData => {
      const responseRate = typeData.totalContacts > 0 
        ? (typeData.respondedContacts / typeData.totalContacts) * 100 
        : 0;
        
      const conversionRate = typeData.totalContacts > 0 
        ? (typeData.convertedContacts / typeData.totalContacts) * 100 
        : 0;
      
      return {
        name: formatCampaignType(typeData.type),
        responseRate: parseFloat(responseRate.toFixed(1)),
        conversionRate: parseFloat(conversionRate.toFixed(1)),
        totalContacts: typeData.totalContacts,
        campaigns: typeData.campaigns
      };
    }).sort((a, b) => b.responseRate - a.responseRate);
  };

  // Format campaign type for display
  const formatCampaignType = (type) => {
    const typeMap = {
      general: 'General Outreach',
      anniversary: 'Anniversary',
      newsletter: 'Newsletter',
      nurture: 'Relationship',
      recruitment: 'Recruitment',
      event: 'Event',
      gift: 'Gift'
    };
    
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const data = processData();
  
  // Colors for the bars
  const responseColor = '#3182CE'; // Blue
  const conversionColor = '#38A169'; // Green

  return (
    <div className="h-96">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            angle={-45} 
            textAnchor="end" 
            height={70}
            interval={0}
          />
          <YAxis 
            yAxisId="left"
            label={{ value: 'Rate (%)', angle: -90, position: 'insideLeft' }}
            domain={[0, 100]}
          />
          <YAxis 
            yAxisId="right" 
            orientation="right" 
            label={{ value: 'Total Contacts', angle: -90, position: 'insideRight' }}
            domain={[0, 'dataMax + 10']}
          />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'totalContacts') return [value, 'Total Contacts'];
              if (name === 'responseRate') return [`${value}%`, 'Response Rate'];
              if (name === 'conversionRate') return [`${value}%`, 'Conversion Rate'];
              return [value, name];
            }}
          />
          <Legend />
          <Bar 
            yAxisId="left"
            dataKey="responseRate" 
            name="Response Rate" 
            fill={responseColor} 
          />
          <Bar 
            yAxisId="left"
            dataKey="conversionRate" 
            name="Conversion Rate" 
            fill={conversionColor} 
          />
          <Bar 
            yAxisId="right"
            dataKey="totalContacts" 
            name="Total Contacts" 
            fill="#CBD5E0" 
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CampaignEffectivenessChart;
